import React, {useMemo} from 'react';
import {Infobox} from '@flixbus/honeycomb-react';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import ContentLoader from '@/Pages/Common/ContentLoader';
import {useBusPartners} from '@/Pages/Partners/hooks/useBusPartners';
import {useBusPartnerCostsQueryParams} from '@/Pages/PartnerCosts/hooks/useBusPartnerCostsQueryParams';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import {useVehicleTypes} from '@/Pages/VehicleTypes/hooks/useVehicleTypes';
import PartnerCostsVehicleCostContent from './PartnerCostsVehicleCostContent/PartnerCostsVehicleCostContent';

const PartnerCostsVehicleCostTab: React.FC = () => {
  const partnerVehicleCost = useAppSelector(
    ({partnerCosts}) => partnerCosts.vehicleCost
  );
  const regionalVehicleCost = useAppSelector(
    ({partnerCosts}) => partnerCosts.countryCost?.vehicleCost
  );
  const partnerVehicleTypeCosts = useAppSelector(
    ({partnerCosts}) => partnerCosts.vehicleTypeCosts.list
  );
  const regionalVehicleTypeCosts = useAppSelector(
    ({partnerCosts}) => partnerCosts.countryCost?.vehicleTypeCosts
  );

  const {countries} = useCountries();
  const {busPartners} = useBusPartners();
  const {vehicleTypes} = useVehicleTypes();

  const {busPartnerId, year} = useBusPartnerCostsQueryParams();
  const busPartner = useMemo(
    () => busPartners?.find(busPartner => busPartner.id === busPartnerId),
    [busPartnerId, busPartners]
  );
  const busPartnerCountry = useMemo(
    () => countries?.find(country => country.id === busPartner?.countryId),
    [busPartner?.countryId, countries]
  );

  if (busPartnerId === null || year === null) {
    return (
      <Infobox appearance="warning">
        <p>
          Select a <strong>partner</strong> and a <strong>calendar year</strong>
        </p>
      </Infobox>
    );
  }

  if (
    !partnerVehicleCost ||
    !partnerVehicleTypeCosts ||
    !busPartner ||
    !busPartnerCountry ||
    !vehicleTypes
  ) {
    return <ContentLoader />;
  }

  return (
    <PartnerCostsVehicleCostContent
      busPartner={busPartner}
      year={year}
      partnerVehicleCost={partnerVehicleCost}
      regionalVehicleCost={regionalVehicleCost}
      vehicleTypes={vehicleTypes}
      partnerVehicleTypeCosts={partnerVehicleTypeCosts}
      regionalVehicleTypeCosts={regionalVehicleTypeCosts}
      currency={busPartner.currency}
      partnerCountry={busPartnerCountry}
    />
  );
};

export default React.memo(PartnerCostsVehicleCostTab);
